import {
  MODULY_DOMYSLNIE_W_TEMPERATIO,
  argumentyPisarza,
  blokadaZakladaniaKarty,
  powodOdmowy,
  zdaniePisarza,
} from '@insolitum/ui';
import {
  Package,
  Plus,
  Search,
  Edit2,
  Trash2,
  X,
  AlertCircle,
  CheckCircle,
  Loader2,
  Save,
  Filter,
  ChevronDown,
  Tag,
  Hash,
  FileText,
  ToggleLeft,
  ToggleRight,
  Thermometer,
  Factory,
  Layers,
  ExternalLink,
  ShoppingCart,
} from 'lucide-react';
import { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { supabase } from '../../lib/supabase';
import { moduleScreenRoute } from '../../modules/registry';
import { useAuth } from '../../providers/AuthProvider';

import { ladunekDodania, zmianyFormularza, type FormularzProduktu } from './produktyZmiany';

// ── Types ───────────────────────────────────────────────────────────────────

interface Product {
  id: string;
  organization_id: string;
  name: string;
  code: string | null;
  category: string | null;
  description: string | null;
  /** Moduł, który kartę ZAŁOŻYŁ (pochodzenie) — nie członkostwo w Temperatio. */
  module_type: string | null;
  module_data: Record<string, unknown> | null;
  is_active: boolean;
  // Kolumny rozszerzenia Temperatio — tu tylko do odczytu (podsumowanie); edytuje je panel Temperatio.
  target_dough_temp_c: number | null;
  slow_mixing_time: number | null;
  fast_mixing_time: number | null;
  requires_preferment: boolean;
  fabrica_recipe_stage_id?: string | null;
  /** Członkostwo w Temperatio — pole wyliczane bazy (`waterandice_w_temperatio`, migracja 839). */
  waterandice_w_temperatio?: boolean;
  created_at: string;
  updated_at: string;
}

interface Category {
  id: string;
  name: string;
  slug: string;
}

type ProductFormData = FormularzProduktu;

// ── Constants ───────────────────────────────────────────────────────────────

const MODULE_TABS = [
  { id: 'all', label: 'Wszystkie', icon: Package, color: 'emerald' },
  { id: 'waterandice', label: 'Temperatio', icon: Thermometer, color: 'cyan' },
  { id: 'fabrica', label: 'Fabrica', icon: Factory, color: 'amber' },
  { id: 'pos', label: 'Kasa', icon: ShoppingCart, color: 'violet' },
  { id: 'other', label: 'Inne', icon: Layers, color: 'gray' },
] as const;

const initialFormData: ProductFormData = {
  name: '',
  code: '',
  category: '',
  description: '',
  module_type: '',
};

/**
 * Ekran „Produkty” Temperatio w powłoce — jedyne okno ustawień Temperatio (plan §4.5, decyzja właściciela
 * z 26.10.2026 „jedno okno”). Ścieżka z rejestru modułów, nie wpisana drugi raz.
 */
const EKRAN_PRODUKTOW_TEMPERATIO = moduleScreenRoute('waterandice', '/products');

/** Moduł zakładający kartę — podpis „Założona w: …” (kasa to karty z synchronizacji GoPOS). */
function getModuleLabel(moduleType: string | null) {
  switch (moduleType) {
    case 'waterandice':
      return 'Temperatio';
    case 'fabrica':
      return 'Fabrica';
    case 'pos':
      return 'kasa';
    default:
      return moduleType || '-';
  }
}

// ── Component ───────────────────────────────────────────────────────────────

export function AdminProductsPage() {
  const { currentOrganization, hasRole } = useAuth();
  const navigate = useNavigate();
  // `?karta=<id>` — przejście „Otwórz kartotekę” z okna karty w Temperatio otwiera od razu okno edycji tej karty.
  const [parametry, ustawParametry] = useSearchParams();

  // Data
  const [products, setProducts] = useState<Product[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Module tab filter
  const [activeModuleTab, setActiveModuleTab] = useState<string>('all');

  // Search & filter
  const [searchQuery, setSearchQuery] = useState('');
  const [filterCategory, setFilterCategory] = useState<string>('all');
  const [filterStatus, setFilterStatus] = useState<'all' | 'active' | 'inactive'>('all');
  const [showFilters, setShowFilters] = useState(false);

  // Modal
  const [showModal, setShowModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);
  const [formData, setFormData] = useState<ProductFormData>(initialFormData);
  // Formularz z chwili otwarcia edycji — edycja wysyła wyłącznie pola zmienione względem niego (UD-23).
  const [formNaWejsciu, setFormNaWejsciu] = useState<ProductFormData | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  // Przełącznik „W Temperatio”. Przy edycji: członkostwo z bazy. Przy zakładaniu: `null`, dopóki człowiek go nie
  // ruszy — wtedy pokazuje wartość startową ze stałej modułów domyślnych i idzie za wyborem modułu.
  const [wTemperatio, setWTemperatio] = useState<boolean | null>(null);
  const [wTemperatioNaWejsciu, setWTemperatioNaWejsciu] = useState<boolean>(false);

  // Delete
  const [deleteTarget, setDeleteTarget] = useState<string | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const openDelete = (id: string) => {
    setDeleteError(null);
    setDeleteTarget(id);
  };
  const closeDelete = () => {
    setDeleteError(null);
    setDeleteTarget(null);
  };

  const canManage = hasRole(['owner', 'admin', 'manager']);

  // Kartoteka prowadzona przez włączone źródło (np. Receptor): nowej karty nie zakłada się w panelu (UD-23).
  // Zdanie bazy wyłącza „Dodaj produkt”; odmowę i tak wydaje baza przy zapisie (polityka zakładania).
  const [blokadaZakladania, setBlokadaZakladania] = useState<string | null>(null);
  useEffect(() => {
    setBlokadaZakladania(null);
    if (!currentOrganization) return;
    let zywy = true;
    void blokadaZakladaniaKarty(
      (nazwa, argumenty) => supabase.rpc(nazwa, argumenty),
      currentOrganization.organization_id
    ).then((zdanie) => {
      if (zywy) setBlokadaZakladania(zdanie);
    });
    return () => {
      zywy = false;
    };
  }, [currentOrganization]);

  // ── Fetch ─────────────────────────────────────────────────────────────

  useEffect(() => {
    if (currentOrganization) {
      fetchProducts();
      fetchCategories();
    }
  }, [currentOrganization]);

  const fetchProducts = async () => {
    if (!currentOrganization) return;
    setIsLoading(true);
    try {
      // Pole wyliczane `waterandice_w_temperatio` — członkostwo liczy baza (839), klient nie liczy reguły drugi raz.
      const { data, error: err } = await supabase
        .from('products')
        .select('*, waterandice_w_temperatio')
        .eq('organization_id', currentOrganization.organization_id)
        .order('name');
      if (err) throw err;
      setProducts(data || []);
    } catch (err) {
      console.error('Error fetching products:', err);
      setError('Nie udało się załadować produktów');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchCategories = async () => {
    if (!currentOrganization) return;
    try {
      const { data, error: err } = await supabase
        .from('categories')
        .select('id, name, slug')
        .eq('organization_id', currentOrganization.organization_id)
        .eq('module_type', 'products')
        .eq('is_active', true)
        .order('sort_order')
        .order('name');
      if (err) throw err;
      setCategories(data || []);
    } catch (err) {
      console.error('Error fetching categories:', err);
    }
  };

  // ── Derived ───────────────────────────────────────────────────────────

  const uniqueCategories = useMemo(() => {
    const set = new Set(products.map((p) => p.category).filter(Boolean) as string[]);
    return Array.from(set).sort();
  }, [products]);

  // Chipy nie są rozłączne (Temperatio A+, 839): „Temperatio” liczy CZŁONKÓW (pole wyliczane bazy), „Fabrica”
  // i „Kasa” — karty założone w Fabrice albo przez synchronizację kasy (`module_type`), „Inne” — żadne z nich.
  // Karta z Receptora jest naraz kartą Fabriki i członkiem Temperatio, więc liczby się nie sumują — i ekran mówi to
  // wprost pod chipami. „Kasa” od przeglądu 26.10.2026: po decyzji właściciela z 26.10 karty kasy są w Temperatio,
  // więc „Inne”, w którym leżały, w OGYY spadło do 0, a 166 kart kasy nie miało filtra po pochodzeniu.
  const wModule = (p: Product, chip: string): boolean => {
    switch (chip) {
      case 'all':
        return true;
      case 'waterandice':
        return p.waterandice_w_temperatio === true;
      case 'fabrica':
        return p.module_type === 'fabrica';
      case 'pos':
        return p.module_type === 'pos';
      default:
        return (
          p.waterandice_w_temperatio !== true &&
          p.module_type !== 'fabrica' &&
          p.module_type !== 'pos'
        );
    }
  };

  const moduleCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const tab of MODULE_TABS) {
      counts[tab.id] = products.filter((p) => wModule(p, tab.id)).length;
    }
    return counts;
  }, [products]);

  const filteredProducts = useMemo(() => {
    return products.filter((p) => {
      if (!wModule(p, activeModuleTab)) return false;

      const q = searchQuery.toLowerCase();
      const matchesSearch =
        !q ||
        p.name.toLowerCase().includes(q) ||
        p.code?.toLowerCase().includes(q) ||
        p.category?.toLowerCase().includes(q) ||
        p.description?.toLowerCase().includes(q);
      const matchesCategory = filterCategory === 'all' || p.category === filterCategory;
      const matchesStatus =
        filterStatus === 'all' ||
        (filterStatus === 'active' && p.is_active) ||
        (filterStatus === 'inactive' && !p.is_active);
      return matchesSearch && matchesCategory && matchesStatus;
    });
  }, [products, activeModuleTab, searchQuery, filterCategory, filterStatus]);

  // ── Handlers ──────────────────────────────────────────────────────────

  const openAddModal = () => {
    setEditingProduct(null);
    setFormNaWejsciu(null);
    setFormData({
      ...initialFormData,
      module_type: activeModuleTab !== 'all' && activeModuleTab !== 'other' ? activeModuleTab : '',
    });
    setWTemperatio(null);
    setWTemperatioNaWejsciu(false);
    setFormError(null);
    setShowModal(true);
  };

  const openEditModal = (product: Product) => {
    setEditingProduct(product);
    const naWejsciu: ProductFormData = {
      name: product.name,
      code: product.code || '',
      category: product.category || '',
      description: product.description || '',
      module_type: product.module_type || '',
    };
    setFormData(naWejsciu);
    setFormNaWejsciu(naWejsciu);
    setWTemperatio(product.waterandice_w_temperatio === true);
    setWTemperatioNaWejsciu(product.waterandice_w_temperatio === true);
    setFormError(null);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingProduct(null);
    setFormData(initialFormData);
    setFormNaWejsciu(null);
    setWTemperatio(null);
    setFormError(null);
  };

  // „Otwórz kartotekę” z okna karty w Temperatio przychodzi z `?karta=<id>` i otwiera od razu okno edycji tej karty —
  // bez drugiego wyszukiwania wśród 489 kart OGYY (przegląd 26.10.2026). Karta spoza listy organizacji — nic; parametr
  // znika z adresu po pierwszym użyciu, więc zamknięte okno nie wraca przy odświeżeniu listy po zapisie.
  const kartaZAdresu = parametry.get('karta');
  useEffect(() => {
    if (!kartaZAdresu || isLoading) return;
    const karta = products.find((p) => p.id === kartaZAdresu);
    if (karta && canManage) openEditModal(karta);
    const reszta = new URLSearchParams(parametry);
    reszta.delete('karta');
    ustawParametry(reszta, { replace: true });
    // `openEditModal` i `ustawParametry` to funkcje komponentu — efekt ma zadziałać raz, po wczytaniu listy.
  }, [kartaZAdresu, isLoading, products]);

  /** Stan przełącznika „W Temperatio” na ekranie: wybór człowieka albo wartość startowa modułu zakładającego. */
  const wTemperatioNaEkranie =
    wTemperatio ?? MODULY_DOMYSLNIE_W_TEMPERATIO.includes(formData.module_type);

  /** Zmiana członkostwa pisarzem rozszerzenia (839) — organizacja jawnie, RLS przepuszcza super-admina wszędzie. */
  const zapiszCzlonkostwo = async (kartaId: string, wartosc: boolean) => {
    const { error: err } = await supabase.rpc(
      'waterandice_produkt_zapisz',
      argumentyPisarza(currentOrganization!.organization_id, kartaId, { w_temperatio: wartosc })
    );
    return err;
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentOrganization) return;

    if (!formData.name.trim()) {
      setFormError('Nazwa produktu jest wymagana');
      return;
    }

    setIsSaving(true);
    setFormError(null);

    try {
      if (editingProduct) {
        // Tylko pola zmienione przez człowieka: karta prowadzona przez system zewnętrzny odmawia zmiany pól
        // prowadzonych (nazwa, kod, aktywność), więc zapis całego formularza dawałby odmowę za pola nietknięte.
        // Kolejność: najpierw pola karty (odmowa = nic nie zapisane), potem członkostwo pisarzem.
        const zmiany = zmianyFormularza(formNaWejsciu ?? formData, formData);
        const zmianaCzlonkostwa = wTemperatioNaEkranie !== wTemperatioNaWejsciu;
        if (Object.keys(zmiany).length === 0 && !zmianaCzlonkostwa) {
          closeModal();
          return;
        }
        if (Object.keys(zmiany).length > 0) {
          const { error: err } = await supabase
            .from('products')
            .update(zmiany)
            .eq('id', editingProduct.id)
            .eq('organization_id', currentOrganization.organization_id);
          if (err) {
            if (err.code === '23505') {
              setFormError('Produkt o tym kodzie już istnieje');
              return;
            }
            throw err;
          }
          // Ponowny zapis po odmowie członkostwa nie wysyła już pól, które właśnie weszły.
          setFormNaWejsciu(formData);
        }
        if (zmianaCzlonkostwa) {
          const err = await zapiszCzlonkostwo(editingProduct.id, wTemperatioNaEkranie);
          if (err) {
            const zdanie = zdaniePisarza(err, 'Nie udało się zmienić „W Temperatio”.');
            setFormError(
              Object.keys(zmiany).length > 0
                ? `Karta zapisana, zmiana „W Temperatio” nie: ${zdanie}`
                : zdanie
            );
            await fetchProducts();
            return;
          }
        }
        setSuccess('Produkt został zaktualizowany');
      } else {
        // Nowa karta: pola karty i moduł zakładający; członkostwo liczy baza z modułu. Pisarz — tylko gdy wybór
        // człowieka różni się od wyniku reguły (pole wyliczane czytane po zapisie, nie liczone tu drugi raz).
        const { data: nowa, error: err } = await supabase
          .from('products')
          .insert([
            { ...ladunekDodania(formData, currentOrganization.organization_id), is_active: true },
          ])
          .select('id, waterandice_w_temperatio')
          .single();
        if (err) {
          if (err.code === '23505') {
            setFormError('Produkt o tym kodzie już istnieje');
            return;
          }
          throw err;
        }
        const zalozona = nowa as { id: string; waterandice_w_temperatio?: boolean } | null;
        if (zalozona && zalozona.waterandice_w_temperatio !== wTemperatioNaEkranie) {
          const bladCzlonkostwa = await zapiszCzlonkostwo(zalozona.id, wTemperatioNaEkranie);
          if (bladCzlonkostwa) {
            // Karta już istnieje — okno się zamyka (ponowne „Dodaj” założyłoby drugą), zdanie idzie na baner.
            await fetchProducts();
            closeModal();
            setError(
              `Karta zapisana, zmiana „W Temperatio” nie: ${zdaniePisarza(bladCzlonkostwa, 'Nie udało się zmienić „W Temperatio”.')}`
            );
            return;
          }
        }
        setSuccess('Produkt został dodany');
      }

      await fetchProducts();
      closeModal();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Error saving product:', err);
      // Odmowa bazy (23001: karta prowadzona przez system zewnętrzny, zakładanie przy włączonym źródle, kod już
      // użyty poza Universe) przychodzi ze zdaniem dla człowieka — każdy inny błąd: zdanie zapasowe.
      setFormError(powodOdmowy(err, 'Nie udało się zapisać produktu'));
    } finally {
      setIsSaving(false);
    }
  };

  // Organizacja jawnie także w zapisie aktywności i w usuwaniu (przegląd 26.10.2026): identyfikator pochodzi z listy
  // zawężonej do organizacji, ale `products_update`/`products_delete` przepuszczają super-admina do KAŻDEJ
  // organizacji, więc granicę trzyma filtr zapytania, nie polityka (plan A+ §2.3).
  const handleToggleActive = async (product: Product) => {
    if (!currentOrganization) return;
    try {
      const { error: err } = await supabase
        .from('products')
        .update({ is_active: !product.is_active })
        .eq('id', product.id)
        .eq('organization_id', currentOrganization.organization_id);
      if (err) throw err;
      await fetchProducts();
      setSuccess(product.is_active ? 'Produkt dezaktywowany' : 'Produkt aktywowany');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Error toggling product:', err);
      // Aktywność karty prowadzonej przez system zewnętrzny zmienia się w tym systemie (23001 ze zdaniem bazy).
      setError(powodOdmowy(err, 'Nie udało się zmienić statusu'));
    }
  };

  const handleDelete = async (id: string) => {
    if (!currentOrganization) return;
    setIsDeleting(true);
    setDeleteError(null);
    try {
      const { error: err } = await supabase
        .from('products')
        .delete()
        .eq('id', id)
        .eq('organization_id', currentOrganization.organization_id);
      if (err) throw err;
      await fetchProducts();
      closeDelete();
      setSuccess('Produkt został usunięty');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Error deleting product:', err);
      // Produkt powiązany z kartą systemu zewnętrznego (wyzwalacz `external_ids_chron_encje`, migracja 804):
      // baza odmawia z nazwą systemu i podpowiedzią, co zrobić zamiast usunięcia. Powód stoi w oknie
      // potwierdzenia, przy przycisku, który go wywołał — baner nad listą bywa poza widokiem przewiniętej tabeli.
      setDeleteError(powodOdmowy(err, 'Nie udało się usunąć produktu'));
    } finally {
      setIsDeleting(false);
    }
  };

  const getModuleColor = (moduleType: string | null) => {
    switch (moduleType) {
      case 'waterandice':
        return 'text-cyan-400 bg-cyan-500/20 border-cyan-500/20';
      case 'fabrica':
        return 'text-amber-400 bg-amber-500/20 border-amber-500/20';
      default:
        return 'text-gray-400 bg-gray-500/20 border-gray-500/20';
    }
  };

  // ── Loading ───────────────────────────────────────────────────────────

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <div className="h-8 w-56 skel" />
            <div className="h-4 w-40 skel mt-2" />
          </div>
          <div className="h-10 w-40 skel" />
        </div>
        <div className="h-10 skel" />
        <div className="card tablecard">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="flex items-center gap-4 p-4 border-b border-aurora-border">
              <div className="h-10 w-10 skel" />
              <div className="flex-1 space-y-2">
                <div className="h-4 w-36 skel" />
                <div className="h-3 w-24 skel" />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // ── Render ────────────────────────────────────────────────────────────

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="pagehead">
        <div>
          <h1 className="pagehead__title flex items-center gap-2">
            <Package className="w-6 h-6 text-emerald-400" />
            Produkty
          </h1>
          <p className="pagehead__sub">
            Centralna kartoteka produktów, surowców i półproduktów (
            <span className="mono">{products.length}</span>)
          </p>
          {canManage && blokadaZakladania && (
            <p className="text-sm text-aurora-muted mt-1" role="note">
              {blokadaZakladania}
            </p>
          )}
        </div>
        {canManage && (
          <div className="pagehead__actions">
            <button
              onClick={openAddModal}
              disabled={!!blokadaZakladania}
              title={blokadaZakladania ?? undefined}
              className="btn btn--primary"
            >
              <Plus className="w-5 h-5" />
              Dodaj produkt
            </button>
          </div>
        )}
      </div>

      {/* Module tabs */}
      <div className="flex gap-2 overflow-x-auto pb-1">
        {MODULE_TABS.map((tab) => {
          const Icon = tab.icon;
          const count = moduleCounts[tab.id] || 0;
          const isActive = activeModuleTab === tab.id;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveModuleTab(tab.id)}
              aria-pressed={isActive}
              className="chip whitespace-nowrap"
            >
              <Icon className="w-4 h-4" />
              {tab.label}
              <span className={`badge ${isActive ? 'badge--brand' : ''}`}>{count}</span>
            </button>
          );
        })}
      </div>
      <p className="text-xs text-aurora-subtle -mt-4">
        Karta może być w kilku modułach — liczby się nie sumują.
      </p>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <div className="statcard">
          <div className="statcard__top">
            <span className="statcard__ico">
              <Package />
            </span>
            <span className="statcard__lbl">Łącznie</span>
          </div>
          <div className="statcard__num">{products.length}</div>
        </div>
        <div className="statcard">
          <div className="statcard__top">
            <span className="statcard__ico bg-green-500/15 text-green-400">
              <CheckCircle />
            </span>
            <span className="statcard__lbl">Aktywnych</span>
          </div>
          <div className="statcard__num">{products.filter((p) => p.is_active).length}</div>
        </div>
        <div className="statcard">
          <div className="statcard__top">
            <span className="statcard__ico">
              <Tag />
            </span>
            <span className="statcard__lbl">Kategorii</span>
          </div>
          <div className="statcard__num">{uniqueCategories.length}</div>
        </div>
        <div className="statcard">
          <div className="statcard__top">
            <span className="statcard__ico bg-amber-500/15 text-amber-400">
              <Hash />
            </span>
            <span className="statcard__lbl">Z kodem</span>
          </div>
          <div className="statcard__num">{products.filter((p) => p.code).length}</div>
        </div>
      </div>

      {/* Messages */}
      {error && (
        <div className="flex items-center gap-2 p-4 bg-red-500/10 border border-red-500/30 rounded-ids-lg text-red-400">
          <AlertCircle className="w-5 h-5 flex-shrink-0" />
          <span className="flex-1">{error}</span>
          <button onClick={() => setError(null)}>
            <X className="w-4 h-4" />
          </button>
        </div>
      )}
      {success && (
        <div className="flex items-center gap-2 p-4 bg-green-500/10 border border-green-500/30 rounded-ids-lg text-green-400">
          <CheckCircle className="w-5 h-5 flex-shrink-0" />
          <span>{success}</span>
        </div>
      )}

      {/* Search & Filters */}
      <div className="space-y-3">
        <div className="flex gap-3">
          <div className="inputwrap flex-1">
            <Search />
            <input
              type="text"
              placeholder="Szukaj po nazwie, kodzie, kategorii, opisie..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="input"
            />
          </div>
          <button
            onClick={() => setShowFilters(!showFilters)}
            aria-pressed={showFilters}
            className={`btn ${showFilters ? 'btn--tonal' : 'btn--secondary'}`}
          >
            <Filter className="w-4 h-4" />
            Filtry
            <ChevronDown
              className={`w-4 h-4 transition-transform ${showFilters ? 'rotate-180' : ''}`}
            />
          </button>
        </div>

        {showFilters && (
          <div className="card p-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="field">
              <label className="field__label">Kategoria</label>
              <select
                value={filterCategory}
                onChange={(e) => setFilterCategory(e.target.value)}
                className="select"
              >
                <option value="all">Wszystkie</option>
                {uniqueCategories.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
            </div>
            <div className="field">
              <label className="field__label">Status</label>
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value as typeof filterStatus)}
                className="select"
              >
                <option value="all">Wszystkie</option>
                <option value="active">Aktywne</option>
                <option value="inactive">Nieaktywne</option>
              </select>
            </div>
          </div>
        )}
      </div>

      {/* Table */}
      {filteredProducts.length === 0 ? (
        <div className="card p-12 text-center">
          <Package className="w-12 h-12 text-aurora-subtle mx-auto mb-4" />
          <h3 className="text-lg font-medium text-aurora-text mb-2">
            {searchQuery || filterCategory !== 'all' || filterStatus !== 'all'
              ? 'Nie znaleziono produktów'
              : 'Brak produktów'}
          </h3>
          <p className="text-aurora-muted mb-4">
            {searchQuery
              ? 'Spróbuj zmienić kryteria wyszukiwania'
              : 'Dodaj pierwszy produkt do katalogu'}
          </p>
          {!searchQuery && canManage && (
            <button
              onClick={openAddModal}
              disabled={!!blokadaZakladania}
              title={blokadaZakladania ?? undefined}
              className="btn btn--primary"
            >
              <Plus className="w-5 h-5" />
              Dodaj produkt
            </button>
          )}
        </div>
      ) : (
        <div className="card tablecard">
          <div className="overflow-x-auto">
            <table className="table">
              <thead>
                <tr>
                  <th>Nazwa</th>
                  <th>Kod</th>
                  <th>Kategoria</th>
                  <th>Moduły</th>
                  <th>Status</th>
                  {canManage && <th className="text-right">Akcje</th>}
                </tr>
              </thead>
              <tbody>
                {filteredProducts.map((product) => (
                  <tr key={product.id} className={!product.is_active ? 'opacity-60' : ''}>
                    <td>
                      <div className="cellname">
                        <span className="w-8 h-8 rounded-ids-md bg-emerald-500/15 grid place-items-center flex-shrink-0">
                          <Package className="w-4 h-4 text-emerald-400" />
                        </span>
                        <div>
                          <p className="text-sm">{product.name}</p>
                          {product.description && (
                            <p className="text-xs font-normal text-aurora-subtle max-w-[200px] truncate">
                              {product.description}
                            </p>
                          )}
                        </div>
                      </div>
                    </td>
                    <td>
                      {product.code ? (
                        <span className="badge mono">
                          <Hash className="w-3 h-3" />
                          {product.code}
                        </span>
                      ) : (
                        <span className="text-sm text-aurora-subtle">-</span>
                      )}
                    </td>
                    <td>
                      {product.category ? (
                        <span className="badge badge--brand">
                          <Tag className="w-3 h-3" />
                          {product.category}
                        </span>
                      ) : (
                        <span className="text-sm text-aurora-subtle">-</span>
                      )}
                    </td>
                    <td>
                      {/* Dwie różne rzeczy, dwie etykiety: pochodzenie karty (moduł zakładający) i członkostwo
                          w Temperatio. Do 839 jedna etykieta „Temperatio” znaczyła pierwsze, a czytała się jak
                          drugie. */}
                      <div className="flex flex-wrap items-center gap-1.5">
                        {product.module_type ? (
                          <span className={`badge ${getModuleColor(product.module_type)}`}>
                            Założona w: {getModuleLabel(product.module_type)}
                          </span>
                        ) : (
                          <span className="text-sm text-aurora-subtle">-</span>
                        )}
                        {product.waterandice_w_temperatio === true && (
                          <span className="badge text-cyan-400 bg-cyan-500/20 border-cyan-500/20">
                            <Thermometer className="w-3 h-3" />W Temperatio
                          </span>
                        )}
                      </div>
                    </td>
                    <td>
                      {canManage ? (
                        <button
                          onClick={() => handleToggleActive(product)}
                          className={`pill ${product.is_active ? 'pill--active' : 'pill--suspended'}`}
                        >
                          {product.is_active ? (
                            <>
                              <ToggleRight className="w-3.5 h-3.5" />
                              Aktywny
                            </>
                          ) : (
                            <>
                              <ToggleLeft className="w-3.5 h-3.5" />
                              Nieaktywny
                            </>
                          )}
                        </button>
                      ) : (
                        <span
                          className={`pill ${product.is_active ? 'pill--active' : 'pill--suspended'}`}
                        >
                          <span className="pin" />
                          {product.is_active ? 'Aktywny' : 'Nieaktywny'}
                        </span>
                      )}
                    </td>
                    {canManage && (
                      <td>
                        <div className="flex items-center justify-end gap-1 rowact">
                          <button
                            onClick={() => openEditModal(product)}
                            className="p-1.5 text-aurora-muted hover:text-aurora-text hover:bg-aurora-surface rounded-ids-md transition-colors"
                            title="Edytuj"
                          >
                            <Edit2 className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => openDelete(product.id)}
                            className="p-1.5 text-aurora-muted hover:text-red-400 hover:bg-red-500/10 rounded-ids-md transition-colors"
                            title="Usuń"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          {/* W trakcie usuwania okno się nie zamyka: wynik (odmowa albo zamknięcie) trafia do okna produktu,
              który go wywołał — zamknięte i otwarte na innym pokazałoby odmowę A przy produkcie B. */}
          <div
            className="absolute inset-0 bg-aurora-scrim"
            onClick={isDeleting ? undefined : closeDelete}
          />
          <div className="relative bg-aurora-surface border border-aurora-border rounded-ids-xl shadow-elev-4 max-w-md w-full">
            <div className="modal__head">
              <h3 className="modal__title">Potwierdzenie usuwania</h3>
            </div>
            <div className="modal__body">
              <p className="text-sm text-aurora-muted">
                Czy na pewno chcesz usunąć ten produkt? Tej operacji nie można cofnąć.
              </p>
              {deleteError && (
                <div
                  className="mt-3 flex items-start gap-2 p-3 bg-red-500/10 border border-red-500/30 rounded-ids-lg text-sm text-red-400"
                  role="alert"
                >
                  <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  <span>{deleteError}</span>
                </div>
              )}
            </div>
            <div className="modal__foot">
              <button onClick={closeDelete} disabled={isDeleting} className="btn btn--ghost">
                Anuluj
              </button>
              <button
                onClick={() => handleDelete(deleteTarget)}
                disabled={isDeleting}
                className="btn btn--danger"
              >
                {isDeleting && <Loader2 className="w-4 h-4 animate-spin" />}
                Usuń
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-aurora-scrim backdrop-blur-sm" onClick={closeModal} />
          <div className="relative bg-aurora-surface rounded-ids-xl shadow-elev-4 max-w-2xl w-full border border-aurora-border max-h-[90vh] overflow-hidden flex flex-col">
            {/* Header */}
            <div className="modal__head">
              <div className="w-10 h-10 bg-emerald-500/15 rounded-ids-md flex items-center justify-center flex-shrink-0">
                <Package className="w-5 h-5 text-emerald-400" />
              </div>
              <div className="flex-1 min-w-0">
                <h2 className="modal__title">
                  {editingProduct ? 'Edytuj produkt' : 'Dodaj produkt'}
                </h2>
                <p className="text-sm text-aurora-muted truncate">
                  {editingProduct ? editingProduct.name : 'Wypełnij dane produktu'}
                </p>
              </div>
              <button onClick={closeModal} className="iconbtn">
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Body */}
            <div className="flex-1 overflow-y-auto p-4 sm:p-6">
              <form id="product-form" onSubmit={handleSave} className="space-y-5">
                {formError && (
                  <div className="bg-red-500/10 border border-red-500/20 rounded-ids-lg p-4 flex items-start gap-3">
                    <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-red-400">{formError}</p>
                  </div>
                )}

                {/* Basic info */}
                <div className="field">
                  <label className="field__label">
                    <div className="flex items-center gap-1.5">
                      <Package className="w-4 h-4" />
                      Nazwa <span className="text-aurora-brand">*</span>
                    </div>
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    placeholder="np. Chleb pszenny, Mąka pszenna T650"
                    className="input"
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="field">
                    <label className="field__label">
                      <div className="flex items-center gap-1.5">
                        <Hash className="w-4 h-4" />
                        Kod produktu
                      </div>
                    </label>
                    <input
                      type="text"
                      value={formData.code}
                      onChange={(e) => setFormData({ ...formData, code: e.target.value })}
                      placeholder="np. PRD-001, SKU123"
                      className="input mono"
                    />
                  </div>
                  <div className="field">
                    <label className="field__label">
                      <div className="flex items-center gap-1.5">
                        <Tag className="w-4 h-4" />
                        Kategoria
                      </div>
                    </label>
                    <select
                      value={formData.category}
                      onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                      className="select"
                    >
                      <option value="">-- Wybierz --</option>
                      {categories.map((cat) => (
                        <option key={cat.id} value={cat.name}>
                          {cat.name}
                        </option>
                      ))}
                      {/* Also show existing categories from products that aren't in the categories table */}
                      {uniqueCategories
                        .filter((c) => !categories.some((cat) => cat.name === c))
                        .map((c) => (
                          <option key={`existing-${c}`} value={c}>
                            {c}
                          </option>
                        ))}
                    </select>
                  </div>
                </div>

                <div className="field">
                  <label className="field__label">
                    <div className="flex items-center gap-1.5">
                      <FileText className="w-4 h-4" />
                      Opis
                    </div>
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    rows={2}
                    placeholder="Opis produktu..."
                    className="textarea resize-none"
                  />
                </div>

                {/* Moduł zakładający — wybierany WYŁĄCZNIE przy zakładaniu karty. Członkostwo w Temperatio bez znacznika
                    liczy się z niego (839), a ochrona 807 go nie obejmuje, więc zmiana pochodzenia przy edycji po cichu
                    zdejmowała kartę z Temperatio i ze starych telefonów albo ją tam dokładała. */}
                <div className="field">
                  <label className="field__label" htmlFor="produkt-modul">
                    <div className="flex items-center gap-1.5">
                      <Layers className="w-4 h-4" />
                      Moduł zakładający
                    </div>
                  </label>
                  {editingProduct ? (
                    <p id="produkt-modul" className="text-sm text-aurora-text">
                      {editingProduct.module_type
                        ? getModuleLabel(editingProduct.module_type)
                        : 'Brak (karta ogólna)'}
                    </p>
                  ) : (
                    <select
                      id="produkt-modul"
                      value={formData.module_type}
                      onChange={(e) => setFormData({ ...formData, module_type: e.target.value })}
                      className="select"
                    >
                      <option value="">-- Brak (ogólny) --</option>
                      <option value="waterandice">Temperatio</option>
                      <option value="fabrica">Fabrica</option>
                    </select>
                  )}
                  <p className="field__hint">
                    {editingProduct
                      ? 'Moduł, w którym kartę założono. Nie zmienia się po założeniu.'
                      : 'Moduł, w którym zakładasz kartę. Po zapisaniu się nie zmienia.'}
                  </p>
                </div>

                {/* Temperatio — członkostwo i podsumowanie. Ustawienia (cel, mieszanie, kwas, tarcie, korekta) mają
                    JEDNO okno, panel Temperatio (decyzja właściciela z 26.10.2026): dwa edytory już się rozjechały —
                    ten nie znał etapu receptury i podstawiał 24 °C. */}
                <div className="bg-cyan-500/5 border border-cyan-500/20 rounded-ids-lg p-4 space-y-3">
                  <div className="flex items-center gap-2">
                    <Thermometer className="w-4 h-4 text-cyan-400" />
                    <h3 className="text-sm font-semibold text-cyan-400">Temperatio</h3>
                  </div>
                  <label className="switch" htmlFor="w_temperatio_central">
                    <input
                      type="checkbox"
                      id="w_temperatio_central"
                      checked={wTemperatioNaEkranie}
                      onChange={(e) => setWTemperatio(e.target.checked)}
                    />
                    <span className="switch__track" />
                    <span className="text-sm text-aurora-text">W Temperatio</span>
                  </label>
                  <p className="field__hint">
                    Karta w Temperatio pojawia się na liście produktów panelu Temperatio, w
                    aplikacji hali i w Nexusie. Wyłączenie nie usuwa karty ani jej historii.
                  </p>
                  {editingProduct && wTemperatioNaWejsciu ? (
                    <dl className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-sm">
                      <dt className="text-aurora-muted">Temperatura docelowa</dt>
                      <dd className="text-aurora-text mono">
                        {editingProduct.target_dough_temp_c !== null
                          ? `${editingProduct.target_dough_temp_c} °C`
                          : 'Bez temperatury docelowej'}
                      </dd>
                      <dt className="text-aurora-muted">Mieszanie</dt>
                      <dd className="text-aurora-text mono">
                        {editingProduct.slow_mixing_time || editingProduct.fast_mixing_time
                          ? `${editingProduct.slow_mixing_time || 0}+${editingProduct.fast_mixing_time || 0} min`
                          : '—'}
                      </dd>
                      <dt className="text-aurora-muted">Kwas</dt>
                      <dd className="text-aurora-text">
                        {editingProduct.requires_preferment ? 'Wymagany' : 'Nie'}
                      </dd>
                      <dt className="text-aurora-muted">Etap receptury</dt>
                      <dd className="text-aurora-text">
                        {editingProduct.fabrica_recipe_stage_id ? 'Z receptury Fabriki' : '—'}
                      </dd>
                    </dl>
                  ) : (
                    <p className="text-sm text-aurora-muted">
                      Temperaturę docelową, mieszanie i kwas ustawisz w Temperatio po zapisaniu
                      karty.
                    </p>
                  )}
                  {editingProduct && wTemperatioNaWejsciu && EKRAN_PRODUKTOW_TEMPERATIO && (
                    <button
                      type="button"
                      onClick={() =>
                        // `?karta=` — Temperatio otwiera od razu okno ustawień tej karty (powłoka przekazuje ten
                        // parametr do ramki), zamiast listy 489 kart OGYY (przegląd 26.10.2026).
                        navigate(
                          `${EKRAN_PRODUKTOW_TEMPERATIO}?karta=${encodeURIComponent(editingProduct.id)}`
                        )
                      }
                      className="btn btn--secondary btn--sm"
                    >
                      <ExternalLink className="w-3.5 h-3.5" />
                      Ustaw w Temperatio
                    </button>
                  )}
                </div>

                {/* Fabrica-specific settings placeholder */}
                {formData.module_type === 'fabrica' && (
                  <div className="bg-amber-500/5 border border-amber-500/20 rounded-ids-lg p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Factory className="w-4 h-4 text-amber-400" />
                      <h3 className="text-sm font-semibold text-amber-400">Ustawienia Fabrica</h3>
                    </div>
                    <p className="text-sm text-aurora-muted">
                      Ustawienia produkcyjne Fabrica będą dostępne po konfiguracji modułu.
                    </p>
                  </div>
                )}
              </form>
            </div>

            {/* Footer */}
            <div className="flex flex-wrap items-center justify-between gap-3 px-4 sm:px-6 py-4 border-t border-aurora-border">
              <p className="text-xs text-aurora-subtle">
                <span className="text-aurora-brand">*</span> Pola wymagane
              </p>
              <div className="flex gap-3">
                <button type="button" onClick={closeModal} className="btn btn--secondary">
                  Anuluj
                </button>
                <button
                  type="submit"
                  form="product-form"
                  disabled={isSaving}
                  className="btn btn--primary min-w-[140px]"
                >
                  {isSaving ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Save className="w-4 h-4" />
                  )}
                  {editingProduct ? 'Zapisz zmiany' : 'Dodaj produkt'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminProductsPage;
